import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Progress,
} from "reactstrap";

const ThankYou = () => {
  const location = useLocation();

  // Extract user info/role passed via router state (fallback to 'candidate')
  const userRole = location.state?.role || "candidate";
  const userName = location.state?.name || "Professional";
  const isEmployer = userRole === "employer";

  useEffect(() => {
    // Dynamic document title update
    document.title = "Welcome to Webvi | Registration Successful";

    // Meta (Facebook) Pixel Event Tracking for Ad Conversions
    // if (window.fbq) {
    //   window.fbq("track", "CompleteRegistration", {
    //     content_name: "Sign Up Success",
    //     user_role: userRole,
    //   });
    // }
  }, [userRole]);

  return (
    <React.Fragment>
      <div className="main-content mt-4 pt-4">
        <div className="page-content">
          <section
            className="py-5 d-flex align-items-center min-vh-100"
            style={{
              backgroundColor: "#f3f6f8",
              backgroundImage:
                "radial-gradient(circle at 50% 0%, rgba(56, 126, 216, 0.08) 0%, transparent 75%)",
            }}
          >
            <Container>
              <Row className="justify-content-center">
                <Col lg={8} md={10}>
                  {/* Main Platform Card */}
                  <Card className="border-0 shadow-lg rounded-4 overflow-hidden bg-white">
                    {/* Header Banner */}
                    <div
                      className="p-4 p-md-5 text-white position-relative"
                      style={{
                        background:
                          "linear-gradient(135deg, #0a66c2 0%, #004182 100%)",
                      }}
                    >
                      <Row className="align-items-center">
                        <Col md={8} className="text-center text-md-start">
                          <span
                            className="badge px-3 py-2 rounded-pill mb-3 text-uppercase fw-semibold"
                            style={{
                              backgroundColor: "rgba(255, 255, 255, 0.2)",
                              letterSpacing: "0.5px",
                              fontSize: "11px",
                            }}
                          >
                            <i className="mdi mdi-check-decagram me-1 text-warning"></i>
                            Account Verified
                          </span>
                          <h1 className="fw-bold mb-2 text-white fs-2">
                            Welcome, {userName}!
                          </h1>

                          <p className="mb-0 text-white-50 fs-15">
                            {isEmployer
                              ? "Your employer workspace is ready. Let's find top talent."
                              : "Your Webvi career account is created. Let's get you hired."}
                          </p>
                          <p className="mb-0 text-white-50 fs-17">
                            To access all pages and features of the portal, please complete your basic profile first.
                          </p>
                        </Col>
                        <Col
                          md={4}
                          className="text-center text-md-end mt-4 mt-md-0"
                        >
                          <div
                            className="d-inline-flex align-items-center justify-content-center bg-white rounded-circle shadow-sm"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <i
                              className="mdi mdi-check-circle text-success"
                              style={{ fontSize: "56px", lineHeight: "1" }}
                            ></i>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <CardBody className="p-4 p-md-5">
                      {/* Onboarding Progress Bar */}
                      <div className="bg-light p-4 rounded-3 border mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-bold text-dark fs-14">
                            <i className="mdi mdi-speedometer text-primary me-1"></i>
                            Profile Completion Status
                          </span>
                          <span className="badge bg-primary-subtle text-primary fw-bold fs-13 px-2 py-1">
                            20% Completed
                          </span>
                        </div>
                        <Progress
                          value={20}
                          color="primary"
                          className="rounded-pill"
                          style={{ height: "8px" }}
                        />
                        <small className="text-muted mt-2 d-block fs-13">
                          {isEmployer
                            ? "Complete your company details and explore hiring plans."
                            : "Add your resume & primary skills to start receiving direct recruiter calls."}
                        </small>
                      </div>

                      {/* Employer Free Job Access Notice */}
                      {isEmployer && (
                        <div
                          className="p-3 mb-4 rounded-3 d-flex align-items-center justify-content-between border"
                          style={{
                            backgroundColor: "#e8f4fd",
                            borderColor: "#b6e0fe",
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <div
                              className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                              style={{ width: "42px", height: "42px" }}
                            >
                              <i className="mdi mdi-gift-outline fs-20"></i>
                            </div>
                            <div>
                              <h6 className="fw-bold text-dark mb-1 fs-14">
                                You get Free Access! 🎁
                              </h6>
                              <p className="text-muted mb-0 fs-13">
                                You have unlocked{" "}
                                <strong>1 Free Job Posting</strong> to get
                                started right away.
                              </p>
                            </div>
                          </div>
                          <Link
                            to="/employer/job/manage"
                            className="btn btn-sm btn-primary fw-semibold text-nowrap ms-2"
                          >
                            Post Free Job{" "}
                            <i className="mdi mdi-arrow-right ms-1"></i>
                          </Link>
                        </div>
                      )}

                      {/* Benefit Cards Section */}
                      <h6
                        className="fw-bold text-uppercase text-muted fs-12 mb-3"
                        style={{ letterSpacing: "0.8px" }}
                      >
                        {isEmployer
                          ? "Why finish setup now?"
                          : "Why top candidates update their profile immediately:"}
                      </h6>

                      <Row className="g-3 mb-4">
                        <Col md={4}>
                          <div className="p-3 border rounded-3 h-100 bg-body-tertiary">
                            <div className="text-primary mb-2">
                              <i className="mdi mdi-eye-outline fs-24"></i>
                            </div>
                            <h6 className="fw-semibold text-dark fs-14 mb-1">
                              {isEmployer
                                ? "View Candidates"
                                : "3x Recruiter Views"}
                            </h6>
                            <p className="text-muted fs-13 mb-0">
                              {isEmployer
                                ? "For access to the candidate database and candidate profiles, you will need to purchase a new paid plan."
                                : "Complete profiles rank top in employer search."}
                            </p>
                          </div>
                        </Col>

                        <Col md={4}>
                          <div className="p-3 border rounded-3 h-100 bg-body-tertiary">
                            <div className="text-primary mb-2">
                              <i className="mdi mdi-flash-outline fs-24"></i>
                            </div>
                            <h6 className="fw-semibold text-dark fs-14 mb-1">
                              {isEmployer ? "Fast Hiring" : "Instant Matches"}
                            </h6>
                            <p className="text-muted fs-13 mb-0">
                              {isEmployer
                                ? "Publish job openings in less than 2 minutes."
                                : "Get matched with jobs tailored to your salary."}
                            </p>
                          </div>
                        </Col>

                        <Col md={4}>
                          <div className="p-3 border rounded-3 h-100 bg-body-tertiary">
                            <div className="text-primary mb-2">
                              <i className="mdi mdi-shield-check-outline fs-24"></i>
                            </div>
                            <h6 className="fw-semibold text-dark fs-14 mb-1">
                              Verified Badge
                            </h6>
                            <p className="text-muted fs-13 mb-0">
                              Build trust with verified credentials and details.
                            </p>
                          </div>
                        </Col>

                        
                      </Row>

                      {/* Role-Based Action Buttons */}
                      <div className="d-grid gap-3 d-sm-flex justify-content-sm-center pt-2">
                        {isEmployer ? (
                          <>
                            {/* Employer Primary CTA */}
                            <Link
                              to="/employer/profile"
                              className="w-100 w-sm-auto"
                            >
                              <Button
                                color="primary"
                                size="sm"
                                className="px-4 py-3 fw-bold w-100 rounded-3 shadow-sm"
                                style={{
                                  backgroundColor: "#0a66c2",
                                  borderColor: "#0a66c2",
                                }}
                              >
                                Update Profile{" "}
                                <i className="mdi mdi-office-building-cog ms-2"></i>
                              </Button>
                            </Link>

                            {/* Employer Secondary CTA */}
                            <Link to="/pricing" className="w-100 w-sm-auto">
                              <Button
                                outline
                                color="secondary"
                                size="sm"
                                className="px-4 py-3 fw-semibold w-100 rounded-3"
                              >
                                View Pricing Plans{" "}
                                <i className="mdi mdi-tag-text-outline ms-2"></i>
                              </Button>
                            </Link>
                          </>
                        ) : (
                          <>
                            {/* Candidate/Employee Primary CTA */}
                            <Link
                              to="/candidate/profile/update"
                              className="w-100 w-sm-auto"
                            >
                              <Button
                                color="primary"
                                size="lg"
                                className="px-4 py-3 fw-bold w-100 rounded-3 shadow-sm"
                                style={{
                                  backgroundColor: "#0a66c2",
                                  borderColor: "#0a66c2",
                                }}
                              >
                                Update Your Profile{" "}
                                <i className="mdi mdi-account-edit-outline ms-2"></i>
                              </Button>
                            </Link>

                            {/* Candidate/Employee Secondary CTA */}
                            <Link to="/jobs" className="w-100 w-sm-auto">
                              <Button
                                outline
                                color="secondary"
                                size="lg"
                                className="px-4 py-3 fw-semibold w-100 rounded-3"
                              >
                                Browse Open Positions{" "}
                                <i className="mdi mdi-briefcase-search-outline ms-2"></i>
                              </Button>
                            </Link>
                          </>
                        )}
                      </div>

                      {/* Trust Footnote */}
                      <div className="mt-5 text-center text-muted fs-13">
                        <i className="mdi mdi-lock-outline text-success me-1"></i>
                        Encrypted Data Protection • Free Registration • Support
                        24/7
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Container>
          </section>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ThankYou;
